import React, { useState, useEffect } from 'react';
import ReactQuill, { Quill as QuillNS } from 'react-quill';
import 'react-quill/dist/quill.snow.css';


// Register font sizes for Quill
const Size = QuillNS.import('formats/size');
Size.whitelist = ['10px', '11px', '12px', '14px', '16px', '18px', '24px', '32px'];
QuillNS.register(Size, true);

// Inject custom CSS for Quill size dropdown and apply pixel sizes to text
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.innerHTML = `
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="10px"]::before { content: '10px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="11px"]::before { content: '11px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="12px"]::before { content: '12px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="14px"]::before { content: '14px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="16px"]::before { content: '16px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="18px"]::before { content: '18px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="24px"]::before { content: '24px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="32px"]::before { content: '32px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="10px"]::before { content: '10px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="11px"]::before { content: '11px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before { content: '12px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="14px"]::before { content: '14px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="16px"]::before { content: '16px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before { content: '18px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="24px"]::before { content: '24px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="32px"]::before { content: '32px'; }

    .ql-size-10px { font-size: 10px !important; }
    .ql-size-11px { font-size: 11px !important; }
    .ql-size-12px { font-size: 12px !important; }
    .ql-size-14px { font-size: 14px !important; }
    .ql-size-16px { font-size: 16px !important; }
    .ql-size-18px { font-size: 18px !important; }
    .ql-size-24px { font-size: 24px !important; }
    .ql-size-32px { font-size: 32px !important; }
  `;
  document.head.appendChild(style);
}

const quillModules = {
  toolbar: [
    [{ 'font': [] }],
    [{ 'size': Size.whitelist }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'script': 'sub'}, { 'script': 'super' }],
    [{ 'header': [1, 2, 3, false] }],
    [{ 'align': [] }],
    ['blockquote', 'code-block'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    ['link', 'image'],
  ]
};

const DEFAULT_BODY = `
  <div style="text-align:center;">
    <h2>Barangay Certificate</h2>
    <p>This is to certify that <strong>{{resident_name}}</strong> of <strong>{{address}}</strong> ...</p>
    <p>Date: {{date}}</p>
  </div>
`;




import { showToast } from '../common/Toast';
import DocumentTypeModal from '../documents/DocumentTypeModal';
import { PlusIcon } from '@heroicons/react/24/outline';

const CertificateTemplateForm = ({ initialData, onSubmit, onClose, documentTypes = [], onDocumentTypeAdded }) => {
  const [name, setName] = useState(initialData?.name || '');
  const [body, setBody] = useState(initialData?.body || DEFAULT_BODY);
  const [documentTypeId, setDocumentTypeId] = useState(initialData?.document_type_id || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDocumentTypeModal, setShowDocumentTypeModal] = useState(false);

  // Replace tabs with 4 non-breaking spaces for HTML formatting
  function preserveTabs(html) {
    return html.replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;');
  }

  // Create or update certificate template using shared API service
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!documentTypeId || !body.trim()) {
      setError('Document type and body are required.');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...(initialData?.id ? { id: initialData.id } : {}),
        document_type_id: Number(documentTypeId),
        body: preserveTabs(body)
      };

      if (onSubmit) {
        await onSubmit(payload);
      }

      showToast(
        initialData?.id ? 'Certificate template updated!' : 'Certificate template added!',
        'success'
      );
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save template.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{
        background: 'rgba(0,0,0,0.25)',
        padding: '32px',
        minHeight: '100vh',
        boxSizing: 'border-box',
        overflowY: 'auto',
        boxShadow: '0 0 32px 8px rgba(0,0,0,0.10)'
      }}
    >
      <div className="relative w-[60vw] max-w-6xl mx-auto shadow-2xl rounded-2xl bg-white" style={{ maxHeight: '90vh' }}>
        <form
          className="bg-white rounded-2xl shadow-2xl p-0 flex flex-col h-full border border-gray-100 animate-fadein"
          onSubmit={handleSubmit}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between px-8 py-5  rounded-t-2xl">
            <h2 className="text-lg font-medium text-gray-700 tracking-tight">
              {initialData?.id ? 'Edit Certificate Template' : 'Add Certificate Template'}
            </h2>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-400 transition-colors p-2 rounded-full focus:outline-none"
              aria-label="Close"
              onClick={onClose}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          {/* Modal Body */}
          <div className="flex-1 px-8 py-6 overflow-y-auto">
            {error && <div className="mb-4 text-red-600 font-semibold">{error}</div>}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="block font-semibold text-gray-700">Document Type</label>
                {!initialData?.id && (
                  <button
                    type="button"
                    onClick={() => setShowDocumentTypeModal(true)}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                  >
                    <PlusIcon className="w-3 h-3 mr-1" />
                    Add New Document Type
                  </button>
                )}
              </div>
              {initialData?.certificate_name && (
                <div className="mb-2 text-blue-700 font-semibold text-lg">{initialData.certificate_name}</div>
              )}
              <select
                className="w-full border border-gray-300 px-4 py-2 rounded-lg text-lg bg-white text-gray-700"
                value={documentTypeId}
                onChange={e => setDocumentTypeId(e.target.value)}
                required
                disabled={!!initialData?.id} // Disable select when editing existing template
              >
                <option value="" disabled>Select document type...</option>
                {(Array.isArray(documentTypes) ? documentTypes : []).map(dt => (
                  <option key={dt.id} value={dt.id}>{dt.label}</option>
                ))}
              </select>
              {!documentTypeId && (
                <span className="text-xs text-red-500 mt-1">Document type is required.</span>
              )}
            </div>

            <div className="mb-2 flex flex-col md:flex-row gap-6">
              <div className="flex-1 flex flex-col">
                <label className="block mb-2 font-semibold text-gray-700">Template Body <span className="text-xs text-gray-400">(use <code>{'{{variable}}'}</code> for dynamic fields)</span></label>
                <div className="rounded-lg border border-gray-200 bg-white overflow-hidden shadow-sm mb-4">
                  <ReactQuill
                    className="flex-1 min-h-[300px]"
                    theme="snow"
                    value={body}
                    onChange={setBody}
                    modules={quillModules}
                    style={{ minHeight: '100px', background: 'white', minWidth: '210mm' }}
                  />
                </div>
              </div>
            </div>
          </div>
          {/* Modal Footer */}
          <div className="flex justify-end gap-3 px-8 py-5 border-t border-gray-100 rounded-b-2xl">
            <button
              type="button"
              className="px-6 py-3 border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 hover:scale-105 active:scale-95"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 border-2 border-transparent rounded-lg text-white font-medium bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-200 hover:scale-105 active:scale-95 relative overflow-hidden group"
              disabled={loading}
            >
              {loading ? 'Saving...' : (initialData?.id ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>

      {/* Document Type Management Modal */}
      <DocumentTypeModal
        open={showDocumentTypeModal}
        onClose={() => setShowDocumentTypeModal(false)}
        onAddType={(newType) => {
          if (onDocumentTypeAdded) {
            onDocumentTypeAdded(newType);
          }
          // Auto-select the newly added document type
          if (newType && newType.id) {
            setDocumentTypeId(newType.id);
          }
        }}
      />
    </div>
  );
};

export default React.memo(CertificateTemplateForm);
